import { useEffect, useRef, useState } from 'react';
import {
    IAgentClientRow,
    IAgentClientsFilters,
    ICreateAgentClientRequest,
    useCreateAgentClientMutation,
    useGetAgentClientsMutation,
    useResetAgentClientPasswordMutation,
} from '../../../store/users/usersApi';
import {
    resolveBillyHasMore,
} from '../../../utils/pagination/billyPagination';
import AgentClientsFilters from './components/AgentClientsFilters';
import AgentClientsTable from './components/AgentClientsTable';
import CreateAgentClientModal from './components/CreateAgentClientModal';
import ResetAgentClientPasswordModal from './components/ResetAgentClientPasswordModal';

const sanitizeFilters = (filters: IAgentClientsFilters) => ({
    ...(filters.search?.trim() ? { search: filters.search.trim() } : {}),
    ...(filters.agent?.trim() ? { agent: filters.agent.trim() as IAgentClientsFilters['agent'] } : {}),
    ...(typeof filters.enabled === 'boolean' ? { enabled: filters.enabled } : {}),
});

const AgentClientsPage = () => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const requestNextPageRef = useRef(false);

    const [paginationState, setPaginationState] = useState({
        pageNumber: 1,
        pageSize: 10,
    });
    const [filters, setFilters] = useState<IAgentClientsFilters>({});
    const [appliedFilters, setAppliedFilters] = useState<IAgentClientsFilters>({});
    const [rows, setRows] = useState<IAgentClientRow[]>([]);
    const [pageData, setPageData] = useState<any>(undefined);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);
    const [resetPasswordClient, setResetPasswordClient] = useState<IAgentClientRow | null>(null);
    const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);
    const [listRefreshKey, setListRefreshKey] = useState(0);
    const [getAgentClients] = useGetAgentClientsMutation();
    const [createAgentClient, { isLoading: creatingClient }] = useCreateAgentClientMutation();
    const [resetAgentClientPassword, { isLoading: resettingPassword }] = useResetAgentClientPasswordMutation();

    useEffect(() => {
        const fetchData = async () => {
            const isFirstPage = paginationState.pageNumber === 1;

            try {
                if (isFirstPage) {
                    setLoading(true);
                } else {
                    setLoadingMore(true);
                }

                const result = await getAgentClients({
                    pageNumber: paginationState.pageNumber,
                    pageSize: paginationState.pageSize,
                    data: appliedFilters,
                }).unwrap();

                const elements = result?.elements || [];
                const page = result?.page;

                setPageData(page);
                setRows((prevRows) => (isFirstPage ? elements : [...prevRows, ...elements]));
                setHasMore(resolveBillyHasMore(page, elements.length, paginationState.pageSize || 10));
            } catch (error) {
                console.error(error);
                if (isFirstPage) {
                    setRows([]);
                    setPageData(undefined);
                }
                setHasMore(false);
            } finally {
                requestNextPageRef.current = false;
                setLoading(false);
                setLoadingMore(false);
            }
        };

        fetchData();
    }, [
        getAgentClients,
        paginationState.pageNumber,
        paginationState.pageSize,
        appliedFilters,
        listRefreshKey,
    ]);

    const resetAndFetchFromFirstPage = (nextFilters: IAgentClientsFilters) => {
        setRows([]);
        setPageData(undefined);
        setHasMore(true);
        requestNextPageRef.current = false;
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
        setAppliedFilters(nextFilters);
        setPaginationState((prev) => ({
            ...prev,
            pageNumber: 1,
        }));
    };

    const onApplyFilters = () => {
        resetAndFetchFromFirstPage(sanitizeFilters(filters));
    };

    const onClearFilters = () => {
        const emptyFilters: IAgentClientsFilters = {};
        setFilters(emptyFilters);
        resetAndFetchFromFirstPage(emptyFilters);
    };

    const onLoadMore = () => {
        if (!hasMore || loadingMore || loading || requestNextPageRef.current) return;
        requestNextPageRef.current = true;
        setPaginationState((prev) => ({
            ...prev,
            pageNumber: (prev.pageNumber || 1) + 1,
        }));
    };

    const refreshFirstPage = () => {
        setRows([]);
        setPageData(undefined);
        setHasMore(true);
        requestNextPageRef.current = false;
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
        setPaginationState((prev) => ({
            ...prev,
            pageNumber: 1,
        }));
        setListRefreshKey((prev) => prev + 1);
    };

    const onCreateClient = async (payload: ICreateAgentClientRequest) => {
        setCreateError(null);
        try {
            await createAgentClient(payload).unwrap();
            setCreateModalOpen(false);
            refreshFirstPage();
            return true;
        } catch (error: any) {
            setCreateError(
                error?.data?.message
                || error?.data?.error
                || error?.error
                || 'Unable to create client. Please try again.',
            );
            return false;
        }
    };

    const onResetPassword = async (password: string) => {
        if (!resetPasswordClient?.username || resetPasswordClient.username === '--') {
            setResetPasswordError('Username is required to reset password.');
            return false;
        }

        setResetPasswordError(null);
        try {
            await resetAgentClientPassword({
                username: resetPasswordClient.username,
                password,
            }).unwrap();
            setResetPasswordClient(null);
            return true;
        } catch (error: any) {
            setResetPasswordError(
                error?.data?.message
                || error?.data?.error
                || error?.error
                || 'Unable to reset password. Please try again.',
            );
            return false;
        }
    };

    return (
        <div className='w-full'>
            <div className='flex flex-col gap-3 md:flex-row md:items-start md:justify-between'>
                <div>
                    <h1 className='text-xl md:text-2xl font-bold text-[#101010] dark:text-primary-darkMode'>
                        Agent Clients
                    </h1>

                    <p className='mt-2 text-sm text-gray-600 dark:text-gray-400'>
                        Review clients connected to Sofia, Leo, Charles, Claire, Rebecca, Filipa, Iris and Martin.
                    </p>
                </div>

                <button
                    type='button'
                    onClick={() => {
                        setCreateError(null);
                        setCreateModalOpen(true);
                    }}
                    className='inline-flex items-center justify-center rounded-xl border border-[#00330f] bg-[#00330f] px-4 py-2 text-sm font-semibold text-white dark:border-white dark:bg-white dark:text-black hover:opacity-90'
                >
                    Create Client
                </button>
            </div>

            <AgentClientsFilters
                totalElements={pageData?.totalElements || rows.length || 0}
                filters={filters}
                loading={loading}
                onChange={setFilters}
                onApply={onApplyFilters}
                onClear={onClearFilters}
            />

            <AgentClientsTable
                rows={rows}
                loading={loading}
                loadingMore={loadingMore}
                hasMore={hasMore}
                scrollContainerRef={scrollContainerRef}
                onLoadMore={onLoadMore}
                onResetPassword={(row) => {
                    setResetPasswordError(null);
                    setResetPasswordClient(row);
                }}
            />

            <CreateAgentClientModal
                open={createModalOpen}
                loading={creatingClient}
                error={createError}
                onClose={() => {
                    setCreateError(null);
                    setCreateModalOpen(false);
                }}
                onSubmit={onCreateClient}
            />

            <ResetAgentClientPasswordModal
                open={!!resetPasswordClient}
                username={resetPasswordClient?.username || ''}
                loading={resettingPassword}
                error={resetPasswordError}
                onClose={() => {
                    setResetPasswordError(null);
                    setResetPasswordClient(null);
                }}
                onSubmit={onResetPassword}
            />
        </div>
    );
};

export default AgentClientsPage;
